import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Rocket,
  Bot,
  Unlock,
  Calendar,
  ShieldCheck,
  MapPin,
  ChevronDown,
  Sun,
} from "lucide-react";

const MAP_BG =
  "https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/UVVzNKZUd1L69rCuLWF8/media/69ecc4ab0d66f2a6651501d5.jpg";

const Hero = () => {
  return (
    <section className="relative flex min-h-[100svh] flex-col overflow-hidden">
      {/* Map background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${MAP_BG})` }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 38%, rgba(255,255,255,0.6) 55%, rgba(255,255,255,0) 78%)",
        }}
      />

      <div className="relative mx-auto flex w-full max-w-6xl flex-1 items-center px-5 py-12 sm:px-8 sm:py-16">
        <div className="grid w-full grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-7">
            <p className="mb-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 px-3.5 py-1.5 text-xs font-black uppercase tracking-widest text-orange-800 shadow-sm ring-1 ring-orange-200/60">
              <Sun className="h-3.5 w-3.5" />
              Spring Campaigns Now Open · One Exterior Cleaner Per Territory
            </p>
            <h1
              className="text-4xl font-black uppercase leading-[1.02] tracking-tight text-brand-black sm:text-5xl lg:text-6xl"
              style={{
                textShadow:
                  "0 1px 2px rgba(255,255,255,0.95), 0 0 18px rgba(255,255,255,0.85)",
              }}
            >
              Win More House &{" "}
              <span
                className="text-brand-blue"
                style={{
                  textShadow:
                    "0 1px 2px rgba(255,255,255,0.95), 0 2px 12px rgba(59,130,246,0.35)",
                }}
              >
                Roof Washing
              </span>{" "}
              Jobs This Spring
            </h1>
            <p
              className="mt-5 max-w-xl text-base leading-relaxed text-[#374151] sm:text-lg"
              style={{ textShadow: "0 1px 2px rgba(255,255,255,0.9)" }}
            >
              We build and manage your Meta campaigns, generate exclusive local
              enquiries and use LEO to follow up 24/7 — so you spend less time
              chasing leads and more time quoting profitable exterior-cleaning
              work.
            </p>

            {/* Guarantee statement */}
            <p
              className="mt-6 max-w-xl rounded-2xl bg-white/85 px-4 py-3 text-sm font-black uppercase leading-snug tracking-wide text-brand-black shadow-sm ring-1 ring-emerald-200/70 backdrop-blur sm:text-[15px]"
            >
              <ShieldCheck className="mr-1.5 inline h-4 w-4 -translate-y-0.5 text-emerald-600" />
              30 Exclusive Local Enquiries In Your First 30 Days —{" "}
              <span className="text-brand-blue">Or We Work For Free</span> Until You Get Them.
            </p>

            <ul className="mt-8 flex flex-col gap-3">
              {[
                { Icon: Rocket, text: "Proven ads & offers built specifically for exterior cleaners." },
                { Icon: Bot, text: "LEO responds, follows up and qualifies enquiries 24/7." },
                { Icon: ShieldCheck, text: "Exclusive enquiries — never sold to other cleaners." },
                { Icon: MapPin, text: "One exterior-cleaning operator per territory." },
              ].map(({ Icon, text }) => (
                <li
                  key={text}
                  className="flex items-center gap-3 text-base font-medium text-brand-black sm:text-lg"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue ring-1 ring-brand-blue/20 backdrop-blur-sm">
                    <Icon className="h-4 w-4" />
                  </span>
                  {text}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col items-stretch justify-start gap-3 sm:flex-row sm:items-center">
              <Link
                to="/offer"
                className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl bg-brand-blue px-7 py-4 text-sm font-black uppercase tracking-wider text-white shadow-xl shadow-blue-300/50 ring-1 ring-blue-700/10 transition-all hover:bg-blue-600 active:scale-[0.99]"
              >
                Check If My Territory Is Available <ArrowRight className="h-5 w-5" />
              </Link>
              <a
                href="#results"
                className="inline-flex min-h-[56px] items-center justify-center rounded-2xl border-2 border-gray-200 bg-white/90 px-6 py-4 text-sm font-black uppercase tracking-wider text-brand-black backdrop-blur transition-all hover:border-brand-blue/40 hover:bg-blue-50/60"
              >
                See Client Results
              </a>
            </div>

            <a
              href="#results"
              aria-label="Scroll down to learn more"
              className="group mt-12 inline-flex items-center gap-2 text-brand-grayMid transition-colors hover:text-brand-blue"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/85 ring-1 ring-gray-200 shadow-sm transition-transform group-hover:translate-y-0.5">
                <ChevronDown className="h-4 w-4" />
              </span>
              <span className="text-[11px] font-black uppercase tracking-[0.22em]">
                Scroll Down
              </span>
            </a>
          </div>

          <div className="hidden lg:col-span-5 lg:block" aria-hidden="true" />
        </div>
      </div>

      {/* Trust strip — pricing shown upfront per brief */}
      <div className="relative w-full border-y border-gray-200 bg-white/85 backdrop-blur">
        <div className="mx-auto max-w-6xl px-5 py-5 sm:px-8 sm:py-6">
          <ul className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4 sm:gap-y-2">
            {[
              { Icon: Calendar, text: "$250 / Week + GST" },
              { Icon: Unlock, text: "No Setup Fee · No Lock-In" },
              { Icon: Rocket, text: "Ad Spend From $30 / Day" },
              { Icon: MapPin, text: "One Operator Per Area" },
            ].map(({ Icon, text }) => (
              <li
                key={text}
                className="inline-flex items-center gap-2.5 text-brand-black sm:justify-center"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue ring-1 ring-brand-blue/20">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-[11px] font-black uppercase leading-tight tracking-wide sm:text-[12px]">
                  {text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default Hero;
